import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryClient } from '..';
import * as chatsApi from '@/api/chats.api';
import type { Message, PaginatedMessages, ReactionType } from '@/types/chats';
import { useMemo } from 'react';

const getLatestMessagesQueryKey = (chatId: string | undefined, search?: string) =>
  ['messages', chatId, search, 'latest'] as const;

const getHistoryMessagesQueryKey = (chatId: string | undefined, search?: string) =>
  ['messages', chatId, search, 'history'] as const;

const compareMessageIds = (leftId: string, rightId: string) => {
  const left = BigInt(leftId);
  const right = BigInt(rightId);
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
};

const mergeMessages = (existing: Message[], incoming: Message[]): Message[] => {
  const messagesById = new Map(existing.map((message) => [message.id, message]));

  for (const message of incoming) {
    const previousMessage = messagesById.get(message.id);
    messagesById.set(message.id, previousMessage ? { ...previousMessage, ...message } : message);
  }

  return Array.from(messagesById.values()).sort((left, right) =>
    compareMessageIds(right.id, left.id),
  );
};

export const useMyChats = () =>
  useQuery({
    queryKey: ['chats'],
    queryFn: chatsApi.fetchMyChats,
  });

export const useInfiniteChatMessages = (
  chatId: string | undefined,
  search?: string,
  limit = 50,
) => {
  const localQueryClient = useQueryClient();
  const latestMessagesQuery = useQuery({
    queryKey: getLatestMessagesQueryKey(chatId, search),
    queryFn: async () => {
      const latestPage = await chatsApi.fetchChatMessages(chatId!, limit, undefined, search);
      const previousData = localQueryClient.getQueryData<PaginatedMessages>(
        getLatestMessagesQueryKey(chatId, search),
      );

      return {
        data: mergeMessages(previousData?.data ?? [], latestPage.data),
        nextCursor: previousData?.nextCursor ?? latestPage.nextCursor,
      };
    },
    enabled: !!chatId,
  });

  const historyMessagesQuery = useInfiniteQuery({
    queryKey: getHistoryMessagesQueryKey(chatId, search),
    queryFn: ({ pageParam }) => chatsApi.fetchChatMessages(chatId!, limit, pageParam, search),
    initialPageParam: latestMessagesQuery.data?.nextCursor ?? undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: false,
    gcTime: 1000 * 60,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const historyMessages = useMemo(
    () =>
      (historyMessagesQuery.data?.pages ?? []).flatMap(
        (messagesPage: PaginatedMessages) => messagesPage.data,
      ),
    [historyMessagesQuery.data?.pages],
  );
  const latestMessages = latestMessagesQuery.data?.data ?? [];
  const data = useMemo(
    () => [...latestMessages, ...historyMessages],
    [historyMessages, latestMessages],
  );

  const hasNextPage = historyMessagesQuery.data?.pages?.length
    ? historyMessagesQuery.hasNextPage
    : Boolean(latestMessagesQuery.data?.nextCursor);

  const fetchNextPage = () => {
    if (!hasNextPage) {
      return Promise.resolve(historyMessagesQuery);
    }

    return historyMessagesQuery.fetchNextPage();
  };

  return {
    ...historyMessagesQuery,
    data,
    hasNextPage,
    fetchNextPage,
    isLoading: latestMessagesQuery.isLoading,
    isFetching: latestMessagesQuery.isFetching || historyMessagesQuery.isFetching,
    isFetchingNextPage: historyMessagesQuery.isFetchingNextPage,
    isError: latestMessagesQuery.isError || historyMessagesQuery.isError,
    error: latestMessagesQuery.error ?? historyMessagesQuery.error,
  };
};

export const useMarkAsRead = () =>
  useMutation({
    mutationFn: (messageIds: string[]) => chatsApi.markAsRead(messageIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });

export const useMarkAllRead = () =>
  useMutation({
    mutationFn: (chatId: string) => chatsApi.markAllRead(chatId),
    onSuccess: (_, chatId) => {
      queryClient.invalidateQueries({ queryKey: ['messages', chatId] });
      queryClient.invalidateQueries({ queryKey: ['chats'] });
    },
  });

export const useToggleReaction = (chatId: string) =>
  useMutation({
    mutationFn: ({ messageId, type }: { messageId: string; type: ReactionType }) =>
      chatsApi.toggleReaction(messageId, type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', chatId] });
    },
  });

export const useHasUnreadMessages = () => {
  const { data: chats = [] } = useMyChats();
  return chats.some((chat) => chat.unreadCount > 0);
};
